import React, { FC, useEffect, useState } from "react";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
  Stack,
  Switch,
  Snackbar,
  Alert,
  Box,
  Button,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import { Link as RouterLink } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../../store";
import {
  getBoardRegistrations,
  fetchBoardRegistrations,
  getTournoi,
  updateNbPlaces,
  changeBoardStatus,
  changeBoardQueueStatus,
  updateRealNbPlaces,
} from "../../../features/tournoi/tournoiSlice";
import { Tableau } from "../../../types";
import EditableField from "../../Base/EditableField";

interface AdminBoardsProps {}

const AdminBoards: FC<AdminBoardsProps> = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const boards = useAppSelector(getBoardRegistrations);
  const tournoi = useAppSelector(getTournoi);
  const dispatch = useAppDispatch();
  const [snackBarOpened, setSnackBarOpened] = useState(false);
  const [snackBarMessage, setSnackBarMessage] = useState("");
  const [expandedBoardId, setExpandedBoardId] = useState<string | false>(false);

  useEffect(() => {
    dispatch(fetchBoardRegistrations());
  }, [dispatch, tournoi?.id]);

  useEffect(() => {
    if (!expandedBoardId) return;
    const stillExists = boards.some((board) => board.id === expandedBoardId);
    if (!stillExists) {
      setExpandedBoardId(false);
    }
  }, [boards, expandedBoardId]);

  const openSnackBar = (message: string) => {
    setSnackBarMessage(message);
    setSnackBarOpened(true);
  };

  const handleCloseSnackBar = (
    event: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }

    setSnackBarOpened(false);
  };

  const generateAccordionColor = (board: Tableau) => {
    if (board.ferme || board.isFull) {
      return isDark ? alpha(theme.palette.error.main, 0.9) : "grey.700";
    }
    if (board.nbParticipants! >= board.realNbPlaces) {
      return isDark
        ? alpha(theme.palette.warning.main, 0.9)
        : theme.palette.warning.main;
    }

    if (board.realNbPlaces - board.nbParticipants! < 6) {
      return isDark
        ? alpha(theme.palette.warning.light, 0.95)
        : theme.palette.warning.dark;
    }
    return isDark
      ? alpha(theme.palette.success.main, 0.9)
      : theme.palette.success.dark;
  };

  return (
    <Stack spacing={2}>
      <Alert
        severity="info"
        variant="outlined"
        sx={{
          borderLeft: `4px solid ${theme.palette.info.main}`,
          ...(isDark
            ? {
                borderColor: alpha(theme.palette.info.light, 0.55),
                bgcolor: alpha(theme.palette.info.main, 0.16),
                color: theme.palette.grey[50],
                "& .MuiAlert-icon": {
                  color: theme.palette.info.light,
                },
              }
            : {
                borderColor: alpha(theme.palette.info.main, 0.35),
                bgcolor: alpha(theme.palette.info.main, 0.06),
                color: theme.palette.text.primary,
                "& .MuiAlert-icon": {
                  color: theme.palette.info.dark,
                },
              }),
        }}
      >
        Pour modifier la définition d&apos;un tableau (libellé, date, critères
        d&apos;éligibilité…) :{" "}
        <Button
          component={RouterLink}
          to="/admin/boards-definitions"
          size="small"
          variant="outlined"
          startIcon={<ArticleOutlinedIcon />}
          sx={{
            ml: 1,
            textTransform: "none",
            fontWeight: 700,
            borderColor: isDark
              ? alpha(theme.palette.info.light, 0.75)
              : alpha(theme.palette.info.main, 0.55),
            color: isDark ? theme.palette.info.light : theme.palette.info.dark,
            "&:hover": {
              borderColor: isDark
                ? theme.palette.info.light
                : theme.palette.info.main,
              bgcolor: isDark
                ? alpha(theme.palette.info.light, 0.12)
                : alpha(theme.palette.info.main, 0.08),
            },
          }}
        >
          Définition des tableaux
        </Button>
      </Alert>
      <Typography variant="h5" component="h1" fontWeight={700}>
        Capacité et ouverture
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Places, ouvert / fermé, file d&apos;attente — pour le tournoi courant.
      </Typography>
      {boards.map((board: Tableau) => (
        <Accordion
          key={board.id}
          elevation={0}
          disableGutters
          expanded={expandedBoardId === board.id}
          onChange={(_, isExpanded) => {
            setExpandedBoardId(isExpanded ? board.id : false);
          }}
          sx={{
            border: "1px solid",
            borderColor: isDark
              ? alpha(theme.palette.divider, 0.5)
              : alpha(theme.palette.divider, 0.9),
            borderRadius: 2,
            bgcolor: isDark
              ? alpha(theme.palette.background.paper, 0.55)
              : theme.palette.background.paper,
            overflow: "hidden",
            "&:before": { display: "none" },
            "& + &": { mt: 1.5 },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ width: "100%", pr: 1 }}
            >
              <Typography sx={{ flexGrow: 1 }}>
                {board.name} - {board.label}
                <Box
                  component="span"
                  sx={{
                    display: "inline-block",
                    backgroundColor: generateAccordionColor(board),
                    color: theme.palette.common.white,
                    px: 1,
                    py: 0.3,
                    borderRadius: 10,
                    marginLeft: "6px",
                    fontWeight: 700,
                    fontSize: "0.78rem",
                    boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, isDark ? 0.35 : 0.12)}`,
                  }}
                >
                  {board.participation?.length}/{board.realNbPlaces}
                </Box>
              </Typography>
              <Button
                component={RouterLink}
                to={`/admin/boards-definitions?edit=${board.id}`}
                size="small"
                onClick={(e) => e.stopPropagation()}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  color: isDark ? theme.palette.secondary.light : theme.palette.primary.main,
                }}
              >
                Définition du tableau
              </Button>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack direction="column" spacing={3} margin={2}>
              <Stack direction="row" spacing={3}>
                <Typography sx={{ minWidth: 220, fontWeight: 500 }}>
                  Nombre de places :
                </Typography>
                <EditableField
                  label={String(board.nbPlace)}
                  type="number"
                  onChange={async (newValue) => {
                    const n = parseInt(newValue, 10);
                    if (Number.isNaN(n) || n < 0) {
                      return;
                    }
                    const resAction = await dispatch(
                      updateNbPlaces({ boardId: board.id, nbPlace: n })
                    );

                    if (updateNbPlaces.fulfilled.match(resAction)) {
                      openSnackBar(
                        `Nombre de places du tableau ${board.name} mis à jour`
                      );
                    }
                  }}
                />
              </Stack>
              <Stack direction="row" spacing={3}>
                <Typography sx={{ minWidth: 220, fontWeight: 500 }}>
                  Nombre de places réelles :
                </Typography>
                <EditableField
                  label={board.realNbPlaces.toString()}
                  type="number"
                  onChange={async (newValue) => {
                    const n = parseInt(newValue, 10);
                    if (Number.isNaN(n) || n < 0) {
                      return;
                    }
                    const resAction = await dispatch(
                      updateRealNbPlaces({
                        boardId: board.id,
                        realNbPlaces: n,
                      })
                    );

                    if (updateRealNbPlaces.fulfilled.match(resAction)) {
                      openSnackBar(
                        `Nombre de places du tableau ${board.name} mis à jour`
                      );
                    }
                  }}
                />
              </Stack>
              <Stack direction="row" spacing={3} margin={2}>
                <Typography sx={{ minWidth: 220, fontWeight: 500 }}>
                  Tableau ouvert :
                </Typography>
                <Switch
                  size="small"
                  checked={!board.ferme}
                  onChange={async (event) => {
                    const resStatusAction = await dispatch(
                      changeBoardStatus({
                        boardId: board.id,
                        closed: !event.target.checked,
                      })
                    );

                    if (changeBoardStatus.fulfilled.match(resStatusAction)) {
                      openSnackBar(
                        event.target.checked
                          ? `Tableau ${board.name} ouvert`
                          : `Tableau ${board.name} fermé`
                      );
                    }
                  }}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: theme.palette.success.main,
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      bgcolor: alpha(theme.palette.success.main, 0.5),
                    },
                  }}
                />
              </Stack>
              <Stack direction="row" spacing={3} margin={2}>
                <Typography sx={{ minWidth: 220, fontWeight: 500 }}>
                  Tableau avec file d&apos;attente :
                </Typography>
                <Switch
                  size="small"
                  checked={board.hasWaitingQueue}
                  onChange={async (event) => {
                    const resStatusAction = await dispatch(
                      changeBoardQueueStatus({
                        boardId: board.id,
                        hasWaitingQueue: event.target.checked,
                      })
                    );

                    if (
                      changeBoardQueueStatus.fulfilled.match(resStatusAction)
                    ) {
                      openSnackBar(
                        event.target.checked
                          ? `File d'attente du tableau ${board.name} ouverte`
                          : `File d'attente du tableau ${board.name} fermée`
                      );
                    }
                  }}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: theme.palette.info.main,
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      bgcolor: alpha(theme.palette.info.main, 0.5),
                    },
                  }}
                />
              </Stack>
            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}
      <Snackbar
        open={snackBarOpened}
        autoHideDuration={6000}
        anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
        onClose={handleCloseSnackBar}
      >
        <Alert
          onClose={handleCloseSnackBar}
          severity="success"
          sx={{ width: "100%" }}
        >
          {snackBarMessage}
        </Alert>
      </Snackbar>
    </Stack>
  );
};

export default AdminBoards;
